/**
 * uploadDocument — POST /api/projects/detaildocuments/upload/.
 * Structurally identical to uploadPhoto, but uses type='document' on
 * the file_queue rows and the /upload/ (not /upload-photo/) endpoint.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getByIndex } from '../../utils/indexedDB';
import {
  BASE_URL,
  multipartHeaders,
  classifyResponse,
  networkErrorResult,
  resolveParentCallLogId,
  blobToArrayBuffer,
} from '../httpHelpers';

export interface UploadDocumentInput {
  visitLocalId?: string;
  projectId: number;
  storeId: number;
  files: File[];
  date?: string;
}

export interface UploadDocumentResponse {
  success: boolean;
  document_ids: number[];
}

interface FileQueueRow {
  id?: number;
  taskUUID: string;
  type: 'photo' | 'document';
  fileData: ArrayBuffer;
  fileName: string;
  mimeType: string;
  syncStatus: 'pending' | 'synced' | 'failed';
  createdAt: string;
}

export const uploadDocumentOp: WriteOp<
  UploadDocumentInput,
  UploadDocumentInput,
  UploadDocumentResponse
> = {
  kind: 'uploadDocument',

  async applyLocal(input, localId) {
    const now = new Date().toISOString();
    for (const file of input.files) {
      const fileData = await blobToArrayBuffer(file);
      const row: FileQueueRow = {
        taskUUID: localId,
        type: 'document',
        fileData,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        syncStatus: 'pending',
        createdAt: now,
      };
      await put(STORES.VISIT_FILE_QUEUE, row);
    }
  },

  serialize(input, localId) {
    return {
      action: 'uploadDocument',
      endpoint: '/api/projects/detaildocuments/upload/',
      method: 'POST',
      payload: {
        visitLocalId: input.visitLocalId,
        projectId: input.projectId,
        storeId: input.storeId,
        date: input.date,
        files: [] as File[],
      },
      priority: 60,
      maxRetries: 5,
      dependsOn: input.visitLocalId ?? null,
      relatedTaskUUID: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as UploadDocumentInput;
    let calllogid: number | null = null;
    if (input.visitLocalId) {
      const resolved = await resolveParentCallLogId(input.visitLocalId);
      if (resolved === undefined) {
        return {
          status: 'pending',
          retriable: true,
          errorMessage: 'Parent visit has not synced yet',
        };
      }
      calllogid = resolved;
    }

    const localId = row.relatedTaskUUID;
    if (!localId) {
      return { status: 'failed', retriable: false, errorMessage: 'uploadDocument missing localId' };
    }
    const fileRows = (await getByIndex<FileQueueRow>(
      STORES.VISIT_FILE_QUEUE,
      'taskUUID',
      localId,
    )) as FileQueueRow[];
    const pending = fileRows.filter((r) => r.syncStatus === 'pending');
    if (pending.length === 0) return { status: 'success', retriable: false };

    const formData = new FormData();
    formData.append('project_id', String(input.projectId));
    formData.append('store_id', String(input.storeId));
    if (calllogid != null) formData.append('calllog_id', String(calllogid));
    if (input.date) formData.append('date', input.date);
    for (const r of pending) {
      const blob = new Blob([r.fileData], { type: r.mimeType });
      formData.append('documents', blob, r.fileName);
    }

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: multipartHeaders(),
        body: formData,
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile(row) {
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const fileRows = (await getByIndex<FileQueueRow>(
      STORES.VISIT_FILE_QUEUE,
      'taskUUID',
      localId,
    )) as FileQueueRow[];
    for (const r of fileRows) {
      await put(STORES.VISIT_FILE_QUEUE, { ...r, syncStatus: 'synced' });
    }
  },

  dependsOn(input) {
    return input.visitLocalId ?? null;
  },
};

export type UploadDocumentOp = typeof uploadDocumentOp;
